export const CLIENT_BILLING_TOKEN_OPERATIONS = {
    reviewImport: 'review_import',
    wbReviewRead: 'wb_review_read',
    templateGeneration: 'template_generation',
    reviewAnalysis: 'review_analysis',
    aiAnswerGeneration: 'ai_answer_generation',
    marketplaceAnswerSend: 'marketplace_answer_send',
} as const;

export type ClientBillingTokenOperation =
    (typeof CLIENT_BILLING_TOKEN_OPERATIONS)[keyof typeof CLIENT_BILLING_TOKEN_OPERATIONS];

export const CLIENT_BILLING_OPERATION_HISTORY_USAGE_KEYS = {
    reviewImport: 'reviewImport',
    reviewAnalysis: 'reviewAnalysis',
    aiAnswerGeneration: 'aiAnswerGeneration',
    marketplaceAnswerSend: 'marketplaceAnswerSend',
    templateGeneration: 'templateGeneration',
    wbReviewRead: 'wbReviewRead',
    other: 'other',
} as const;

export type ClientBillingOperationHistoryUsageKey =
    (typeof CLIENT_BILLING_OPERATION_HISTORY_USAGE_KEYS)[keyof typeof CLIENT_BILLING_OPERATION_HISTORY_USAGE_KEYS];

export const CLIENT_BILLING_OPERATION_HISTORY_USAGE_KEY_ORDER = [
    CLIENT_BILLING_OPERATION_HISTORY_USAGE_KEYS.reviewImport,
    CLIENT_BILLING_OPERATION_HISTORY_USAGE_KEYS.reviewAnalysis,
    CLIENT_BILLING_OPERATION_HISTORY_USAGE_KEYS.aiAnswerGeneration,
    CLIENT_BILLING_OPERATION_HISTORY_USAGE_KEYS.marketplaceAnswerSend,
    CLIENT_BILLING_OPERATION_HISTORY_USAGE_KEYS.templateGeneration,
    CLIENT_BILLING_OPERATION_HISTORY_USAGE_KEYS.wbReviewRead,
    CLIENT_BILLING_OPERATION_HISTORY_USAGE_KEYS.other,
] as const satisfies readonly ClientBillingOperationHistoryUsageKey[];

export const CLIENT_BILLING_TOKEN_OPERATION_LABELS_RU: Record<ClientBillingTokenOperation, string> = {
    [CLIENT_BILLING_TOKEN_OPERATIONS.reviewImport]: 'Импорт отзывов',
    [CLIENT_BILLING_TOKEN_OPERATIONS.wbReviewRead]: 'Чтение отзывов WB',
    [CLIENT_BILLING_TOKEN_OPERATIONS.templateGeneration]: 'Анализ ИИ-стратегии',
    [CLIENT_BILLING_TOKEN_OPERATIONS.reviewAnalysis]: 'ИИ-анализ отзывов',
    [CLIENT_BILLING_TOKEN_OPERATIONS.aiAnswerGeneration]: 'Генерация ИИ-ответов',
    [CLIENT_BILLING_TOKEN_OPERATIONS.marketplaceAnswerSend]: 'Отправка ответов на маркетплейс',
};

export const CLIENT_BILLING_OPERATION_HISTORY_USAGE_LABELS_RU: Record<ClientBillingOperationHistoryUsageKey, string> = {
    [CLIENT_BILLING_OPERATION_HISTORY_USAGE_KEYS.reviewImport]: 'Импорт отзывов',
    [CLIENT_BILLING_OPERATION_HISTORY_USAGE_KEYS.reviewAnalysis]: 'Анализ отзыва',
    [CLIENT_BILLING_OPERATION_HISTORY_USAGE_KEYS.aiAnswerGeneration]: 'Генерация ИИ-ответа',
    [CLIENT_BILLING_OPERATION_HISTORY_USAGE_KEYS.marketplaceAnswerSend]: 'Отправка ответа',
    [CLIENT_BILLING_OPERATION_HISTORY_USAGE_KEYS.templateGeneration]: 'ИИ-анализ стратегии',
    [CLIENT_BILLING_OPERATION_HISTORY_USAGE_KEYS.wbReviewRead]: 'Оплата WB API',
    [CLIENT_BILLING_OPERATION_HISTORY_USAGE_KEYS.other]: 'Другое',
};

export const CLIENT_BILLING_OPERATION_HISTORY_USAGE_KEY_BY_OPERATION: Record<
    ClientBillingTokenOperation,
    ClientBillingOperationHistoryUsageKey
> = {
    [CLIENT_BILLING_TOKEN_OPERATIONS.reviewImport]: CLIENT_BILLING_OPERATION_HISTORY_USAGE_KEYS.reviewImport,
    [CLIENT_BILLING_TOKEN_OPERATIONS.reviewAnalysis]: CLIENT_BILLING_OPERATION_HISTORY_USAGE_KEYS.reviewAnalysis,
    [CLIENT_BILLING_TOKEN_OPERATIONS.aiAnswerGeneration]:
        CLIENT_BILLING_OPERATION_HISTORY_USAGE_KEYS.aiAnswerGeneration,
    [CLIENT_BILLING_TOKEN_OPERATIONS.marketplaceAnswerSend]:
        CLIENT_BILLING_OPERATION_HISTORY_USAGE_KEYS.marketplaceAnswerSend,
    [CLIENT_BILLING_TOKEN_OPERATIONS.templateGeneration]:
        CLIENT_BILLING_OPERATION_HISTORY_USAGE_KEYS.templateGeneration,
    [CLIENT_BILLING_TOKEN_OPERATIONS.wbReviewRead]: CLIENT_BILLING_OPERATION_HISTORY_USAGE_KEYS.wbReviewRead,
};

export const isClientBillingTokenOperation = (value: unknown): value is ClientBillingTokenOperation =>
    typeof value === 'string' &&
    Object.values(CLIENT_BILLING_TOKEN_OPERATIONS).includes(value as ClientBillingTokenOperation);

export const getClientBillingTokenOperationLabel = (operation: string, fallback = operation): string =>
    isClientBillingTokenOperation(operation) ? CLIENT_BILLING_TOKEN_OPERATION_LABELS_RU[operation] : fallback;

export const getClientBillingOperationHistoryUsageKey = (operation: string): ClientBillingOperationHistoryUsageKey =>
    isClientBillingTokenOperation(operation)
        ? CLIENT_BILLING_OPERATION_HISTORY_USAGE_KEY_BY_OPERATION[operation]
        : CLIENT_BILLING_OPERATION_HISTORY_USAGE_KEYS.other;
